"use client";

import React, { useEffect, useState } from "react";
import Header from "../../components/IntranetHeader";
import Footer from "../../components/Footer";
import { supabase } from "../../supabaseClient";
import { useFeatureFlags } from "../../contexts/FeatureFlagsContext";

export default function Complaint() {
  const { isEnabled } = useFeatureFlags();
  const complaintSubmissionEnabled = isEnabled("feature.staff_complaint_submission");
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");

  const [form, setForm] = useState({
    reporter_name: "",
    complained_about: "",
    complaint_text: "",
    submitted_at: "",
  });

  // Auto fill submission time
  useEffect(() => {
    const now = new Date();
    const iso = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 16);

    setForm((f) => ({ ...f, submitted_at: iso }));
  }, []);

  const handleChange = (field, value) => {
    setForm((f) => ({ ...f, [field]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!complaintSubmissionEnabled) {
      setErrorMsg("Complaint submissions are currently disabled by the developer.");
      return;
    }
    setErrorMsg("");

    // At least ONE main field must be filled
    const hasAnyInput =
      form.reporter_name.trim() !== "" ||
      form.complained_about.trim() !== "" ||
      form.complaint_text.trim() !== "";

    if (!hasAnyInput) {
      setErrorMsg(
        "Please fill in at least one field before submitting the complaint.",
      );
      return;
    }

    setLoading(true);

    const { error } = await supabase.from("complaints").insert([
      {
        reporter_name: form.reporter_name || null,
        complained_about: form.complained_about || null,
        complaint_text: form.complaint_text || null,
        submitted_at: new Date(form.submitted_at),
      },
    ]);

    if (error) {
      console.error(error);
      setErrorMsg("There was a problem submitting the complaint.");
    } else {
      alert("Complaint submitted successfully.");

      const now = new Date();
      const iso = new Date(now.getTime() - now.getTimezoneOffset() * 60000)
        .toISOString()
        .slice(0, 16);

      setForm({
        reporter_name: "",
        complained_about: "",
        complaint_text: "",
        submitted_at: iso,
      });
    }

    setLoading(false);
  };

  return (
    <div className="flex flex-col min-h-screen">
      <Header />

      <main className="nhsuk-width-container nhsuk-u-padding-4">
        <h1 className="nhsuk-heading-l">Submit a Complaint</h1>

        <p className="nhsuk-body">
          You may submit this complaint anonymously, or provide your name if you
          wish. All fields are optional, however you must complete at least one
          field to submit.
        </p>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Your details (optional / anonymous) */}
          <div>
            <label className="nhsuk-label">
              Your name (optional – leave blank to remain anonymous)
            </label>
            <input
              className="nhsuk-input nhsuk-u-width-full"
              value={form.reporter_name}
              onChange={(e) => handleChange("reporter_name", e.target.value)}
              placeholder="Your name (optional)"
            />
          </div>

          {/* Who the complaint is about */}
          <div>
            <label className="nhsuk-label">
              Who are you making this complaint about?
            </label>
            <input
              className="nhsuk-input nhsuk-u-width-full"
              value={form.complained_about}
              onChange={(e) => handleChange("complained_about", e.target.value)}
              placeholder="Staff name, department, or person involved (optional)"
            />
          </div>

          {/* What the complaint is about */}
          <div>
            <label className="nhsuk-label">What is your complaint?</label>
            <textarea
              className="nhsuk-textarea"
              rows={6}
              value={form.complaint_text}
              onChange={(e) => handleChange("complaint_text", e.target.value)}
              placeholder="Please describe what happened (optional)"
            />
          </div>

          {/* Submission time */}
          <div>
            <label className="nhsuk-label">Time submitted</label>
            <input
              type="datetime-local"
              className="nhsuk-input"
              value={form.submitted_at}
              disabled
            />
          </div>

          {errorMsg && <p className="nhsuk-error-message">{errorMsg}</p>}

          {!complaintSubmissionEnabled && <div className="nhsuk-inset-text">Complaint submissions are currently disabled.</div>}
          <button
            className="nhsuk-button nhsuk-u-width-full"
            disabled={loading || !complaintSubmissionEnabled}
          >
            {loading ? "Submitting…" : "Submit complaint"}
          </button>
        </form>
      </main>

      <Footer />
    </div>
  );
}
